/**
 * SentryErrorBoundary - Global error boundary with Sentry integration
 *
 * Wraps the entire application to catch unhandled errors and report them to Sentry.
 * Displays the ErrorFallback UI when an error occurs.
 */

import * as Sentry from '@sentry/react';
import { ErrorFallback } from './ErrorFallback';

export interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback component to override default */
  fallback?: React.ReactElement;
}

/**
 * Global error boundary that reports errors to Sentry
 * Use this as the outermost error boundary in your app
 */
export function SentryErrorBoundary({
  children,
  fallback,
}: SentryErrorBoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) =>
        fallback || (
          <ErrorFallback
            error={error instanceof Error ? error : new Error(String(error))}
            resetError={resetError}
            title="Oväntat fel"
          />
        )
      }
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('errorBoundary', 'global');
        scope.setLevel('error');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default SentryErrorBoundary;
